import { executeJSmodules } from '@archetype-themes/scripts/helpers/utils'
import { EVENTS, publish } from '@archetype-themes/utils/pubsub'

/*============================================================================
  QuickAdd
  - Setup quick add buttons/forms on a product grid item
==============================================================================*/
class QuickAdd extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      quickAddBtn: '[data-single-variant-quick-add]',
      quickAddHolder: '[data-tool-tip-content]'
    }

    this.initialized = false
  }

  connectedCallback() {
    if (!this.initialized) {
      this.tryInit()
      this.initialized = true
    }
  }

  tryInit() {
    console.log('QuickAdd: tryInit() called', this)
    const quickAddBtn = this.querySelector(this.selectors.quickAddBtn)
    console.log('QuickAdd: quickAddBtn found:', quickAddBtn)
    
    if (quickAddBtn) {
      this.attachClickHandler(quickAddBtn)
    } else {
      this.setupTooltipListeners()
      this.observeForButton()
      
      requestAnimationFrame(() => {
        const retryBtn = this.querySelector(this.selectors.quickAddBtn)
        if (retryBtn) {
          console.log('QuickAdd: Found button on retry')
          this.attachClickHandler(retryBtn)
        }
      })
    }
  }

  attachClickHandler(btn) {
    if (btn.hasAttribute('data-quick-add-initialized')) {
      return
    }
    btn.addEventListener('click', this.addToCart.bind(this))
    btn.setAttribute('data-quick-add-initialized', 'true')
    console.log('QuickAdd: addEventListener("click") attached to quickAddBtn')
  }

  observeForButton() {
    const quickAddBtn = this.querySelector(this.selectors.quickAddBtn)
    if (quickAddBtn) {
      this.attachClickHandler(quickAddBtn)
      return
    }

    const observer = new MutationObserver(() => {
      const quickAddBtn = this.querySelector(this.selectors.quickAddBtn)
      if (quickAddBtn) {
        this.attachClickHandler(quickAddBtn)
        observer.disconnect()
      }
    })

    observer.observe(this, {
      childList: true,
      subtree: true
    })

    setTimeout(() => {
      observer.disconnect()
    }, 10000)
  }

  setupTooltipListeners() {
    if (this.tooltipListenersAttached) {
      return
    }

    this.addEventListener('tooltip:interact', async (e) => {
      if (e.detail.context === 'QuickAdd') {
        if (!this.quickAddData) {
          this.quickAddData = await this.loadQuickAddForm(e)
        }
      }
    })
    console.log('QuickAdd: addEventListener("tooltip:interact") attached')

    this.addEventListener('tooltip:open', async (e) => {
      if (e.detail.context === 'QuickAdd') {
        if (!this.quickAddData) {
          this.quickAddData = await this.loadQuickAddForm(e)
        }

        const quickAddContainer = document.querySelector(this.selectors.quickAddHolder)
        quickAddContainer.innerHTML = this.quickAddData.outerHTML

        document.dispatchEvent(new CustomEvent('quickshop:opened'))

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init()
        }

        const scripts = document.querySelectorAll(`tool-tip [data-product-id="${this.prodId}"] script[type="module"]`)
        executeJSmodules(scripts)
      }
    })
    console.log('QuickAdd: addEventListener("tooltip:open") attached')
    
    this.tooltipListenersAttached = true
  }

  addToCart(evt) {
    const btn = evt.currentTarget
    const visibleBtn = btn.querySelector('.btn')
    const id = btn.dataset.id
    visibleBtn.classList.add('btn--loading')

    const data = {
      items: [
        {
          id: id,
          quantity: 1
        }
      ]
    }

    const endpoint = 'cart/add.js'
    fetch(window.Shopify.routes.root + endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 422 || data.status === 'bad_request') {
          if (data.description) {
            alert(data.description)
          }
        } else {
          publish(EVENTS.ajaxProductAdded, {
            detail: {
              product: data,
              addToCartBtn: btn
            }
          })
        }

        visibleBtn.classList.remove('btn--loading')
      })
  }

  async loadQuickAddForm(evt) {
    const gridItem = evt.currentTarget.closest('.grid-product')
    const handle = gridItem.firstElementChild.getAttribute('data-product-handle')
    this.prodId = gridItem.firstElementChild.getAttribute('data-product-id')

    let url = `${window.Shopify.routes.root}/products/${handle}?view=form`

    // remove double `/` in case shop might have /en or language in URL
    url = url.replace('//', '/')

    try {
      const response = await fetch(url)
      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const div = doc.querySelector(`.page-content[data-product-id="${this.prodId}"]`)
      this.processHTML(div)

      /**
       * @event quickadd:loaded:${productId}
       * @description Triggered when the quick add form is loaded.
       */
      window.dispatchEvent(new CustomEvent(`quickadd:loaded:-${this.prodId}`))

      /**
       * @event quickadd:loaded
       * @description Triggered when the quick add form is loaded.
       * @param {string} detail.productId - The product ID.
       * @param {string} detail.handle - The product handle.
       */
      document.dispatchEvent(
        new CustomEvent('quickadd:loaded', {
          detail: {
            productId: this.prodId,
            handle: handle
          }
        })
      )

      return div
    } catch (error) {
      console.error('Error:', error)
    }
  }

  processHTML(productElement) {
    this.removeBreadcrumbs(productElement)
    this.preventVariantURLSwitching(productElement)
  }

  removeBreadcrumbs(productElement) {
    const breadcrumbs = productElement.querySelector('.breadcrumb')
    if (!breadcrumbs) return

    breadcrumbs.remove()
  }

  preventVariantURLSwitching(productElement) {
    const variantPicker = productElement.querySelector('block-variant-picker')
    if (!variantPicker) return

    variantPicker.removeAttribute('data-update-url')
  }
}

if (!customElements.get('quick-add')) {
  customElements.define('quick-add', QuickAdd)
  console.log('QuickAdd: custom element defined')
} else {
  console.log('QuickAdd: custom element already defined')
}

function handleQuickAddClick(btn) {
  // code added by cab dev: Prevent duplicate add-to-cart requests
  if (btn.hasAttribute('data-adding-to-cart')) {
    console.log('QuickAdd: Button already processing (module), ignoring')
    return
  }
  
  const visibleBtn = btn.querySelector('.btn')
  const id = btn.dataset.id
  
  if (!id) {
    console.error('QuickAdd: No product ID found on button')
    return
  }
  
  // code added by cab dev: Mark button as processing to prevent duplicate clicks
  btn.setAttribute('data-adding-to-cart', 'true')
  
  if (visibleBtn) {
    visibleBtn.classList.add('btn--loading')
  }

  const data = {
    items: [
      {
        id: id,
        quantity: 1
      }
    ]
  }

  const endpoint = 'cart/add.js'
  fetch(window.Shopify.routes.root + endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.status === 422 || data.status === 'bad_request') {
        if (data.description) {
          alert(data.description)
        }
      } else {
        publish(EVENTS.ajaxProductAdded, {
          detail: {
            product: data,
            addToCartBtn: btn
          }
        })
      }

      if (visibleBtn) {
        visibleBtn.classList.remove('btn--loading')
      }
      // code added by cab dev: Remove processing flag after successful add
      btn.removeAttribute('data-adding-to-cart')
    })
    .catch((error) => {
      console.error('QuickAdd: Error adding to cart:', error)
      if (visibleBtn) {
        visibleBtn.classList.remove('btn--loading')
      }
      // code added by cab dev: Remove processing flag on error
      btn.removeAttribute('data-adding-to-cart')
    })
}

// code added by cab dev: Document-level event delegation with duplicate prevention check
document.addEventListener('click', function(e) {
  const btn = e.target.closest('[data-single-variant-quick-add]')
  if (btn && !btn.hasAttribute('data-adding-to-cart')) {
    e.preventDefault()
    e.stopPropagation()
    console.log('QuickAdd: Click detected via document delegation (module)', btn)
    handleQuickAddClick(btn)
  }
}, true)
